import { isoAndUnixZodSchema } from "@@/shared/date-time/iso-and-unix";
import { orderIdZodSchema } from "@@/shared/orders/order-id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import type { SocketEventBase } from "@@/shared/socket-events/socket-event-base";
import { createSocketEventBase } from "@@/shared/socket-events/socket-event-base";
import { isUserId, userIdZodSchema } from "@@/shared/users/user-id";
import { exists } from "@@/utilities/exists";
import z from "zod";
import type { Message } from "../message";
import { messageIdZodSchema } from "../message-id";

type TypeName = "message-event";
type MessageCreatedEventName = "message-created";

const messageReferencesZodObject = z.object({
    orderId: orderIdZodSchema,
    providerId: providerIdZodSchema.nullish(), // providerId is optional
    customerId: userIdZodSchema.nullish(), // customerId is optional
});

const messageCreatedEventDataZodObject = z.object({
    _happenedAt: isoAndUnixZodSchema,
    eventType: z.literal("message-created"),
    messageId: messageIdZodSchema,
    references: messageReferencesZodObject,
});

type MessageCreatedEventData = z.infer<typeof messageCreatedEventDataZodObject>;
type MessageCreatedSocketEvent = SocketEventBase<
    TypeName,
    MessageCreatedEventName,
    MessageCreatedEventData
>;

const messageCreatedSocketEventFactory = (
    message: Message,
): MessageCreatedSocketEvent => {
    return createSocketEventBase<
        TypeName,
        MessageCreatedEventName,
        MessageCreatedEventData
    >({
        type: "message-event:message-created",
        triggeredBy: message._id || "unknown",
        for: [
            message.ownerId, // ownerId is orderId
            message.providerId,
            ...(isUserId(message.author) ? [message.author] : []),
            message.replacedById, // replacedById is the message that replaced this message
        ].filter(exists),
        triggeredAt: message._createdAt,
        data: {
            _happenedAt: message._createdAt,
            eventType: "message-created",
            messageId: message._id,
            references: {
                orderId: message.ownerId,
                providerId: message.providerId,
                ...(isUserId(message.author)
                    ? { customerId: message.author }
                    : {}),
            },
        } satisfies MessageCreatedEventData,
    });
};

export { messageCreatedEventDataZodObject, messageCreatedSocketEventFactory };
export type { MessageCreatedSocketEvent };
